/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiForm,
  EuiFormRow,
  EuiSpacer,
  EuiTextArea,
  EuiTitle,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { CuiAlert } from '@modules/cui/Alert'

import { messages } from './messages'

import type { ConvertOrganizationFlyoutProps, ConvertProps } from './types'
import type { FunctionComponent } from 'react'

const ConvertOrganizationFlyout: FunctionComponent<ConvertOrganizationFlyoutProps> = ({
  convertOrganization,
  convertOrganizationRequest,
  onClose,
  convertType,
  email,
}) => {
  const [reason, setReason] = useState<string>('')
  const [issue, setIssue] = useState<string>('')

  const onSubmit = () => {
    const convertRequest: ConvertProps = {
      issue: issue?.trim(),
      reason: reason?.trim(),
      email,
    }

    convertOrganization(convertRequest).then(onClose)
  }

  const flyoutTitleId = useGeneratedHtmlId({
    prefix: 'convertOrganizationTitle',
  })

  const convertMessage = {
    activate: messages.activateOrganization,
    deactivate: messages.deactivateOrganization,
  }[convertType]

  return (
    <EuiFlyout
      size='m'
      maxWidth={0}
      ownFocus={true}
      onClose={onClose}
      aria-labelledby={flyoutTitleId}
    >
      <EuiFlyoutHeader hasBorder={true}>
        <EuiTitle size='m'>
          <h2 id={flyoutTitleId}>
            <FormattedMessage {...convertMessage} />
          </h2>
        </EuiTitle>
      </EuiFlyoutHeader>

      <EuiFlyoutBody>
        <EuiFormRow
          label={<FormattedMessage {...messages.convertOrganizationIssue} />}
          helpText={<FormattedMessage {...messages.convertOrganizationIssueHelp} />}
        >
          <EuiTextArea
            data-test-id='convert-organization-issue'
            value={issue}
            rows={3}
            onChange={(e) => {
              setIssue(e.target.value)
            }}
            readOnly={convertOrganizationRequest.inProgress}
          />
        </EuiFormRow>

        <EuiSpacer />

        <EuiForm>
          <EuiFormRow
            label={<FormattedMessage {...messages.convertOrganizationReason} />}
            helpText={<FormattedMessage {...messages.convertOrganizationReasonHelp} />}
          >
            <EuiTextArea
              data-test-id='convert-organization-reason'
              value={reason}
              rows={3}
              onChange={(e) => {
                setReason(e.target.value)
              }}
              readOnly={convertOrganizationRequest.inProgress}
            />
          </EuiFormRow>
        </EuiForm>

        {convertOrganizationRequest.error && (
          <Fragment>
            <EuiSpacer size='m' />

            <CuiAlert iconType='alert' type='error'>
              {convertOrganizationRequest.error}
            </CuiAlert>
          </Fragment>
        )}
      </EuiFlyoutBody>

      <EuiFlyoutFooter>
        <EuiFlexGroup justifyContent='spaceBetween'>
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty iconType='cross' onClick={onClose} flush='left'>
              <FormattedMessage {...messages.cancel} />
            </EuiButtonEmpty>
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            <EuiButton
              data-test-id='confirm-convert-organization-button'
              onClick={onSubmit}
              fill={true}
              isLoading={convertOrganizationRequest.inProgress}
            >
              <FormattedMessage {...messages[`${convertType}Organization`]} />
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </EuiFlyout>
  )
}

export default ConvertOrganizationFlyout
